import React, { useEffect, useId, useRef, useState } from 'react';
import { X, Youtube, Play, Pause, ImageOff } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import {
  getExerciseMedia,
  getInstructions,
  youtubeSearchUrl,
  type ExerciseMedia,
  type DemoLang,
} from '../utils/exerciseMedia';

interface ExerciseDemoModalProps {
  exerciseName: string | null;
  onClose: () => void;
}

type LoadState = 'loading' | 'ready' | 'notfound' | 'error';

const LANG_KEY = 'exercise-demo-lang';
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function readStoredLang(): DemoLang {
  try {
    return localStorage.getItem(LANG_KEY) === 'en' ? 'en' : 'es';
  } catch {
    return 'es';
  }
}

export function ExerciseDemoModal({ exerciseName, onClose }: ExerciseDemoModalProps) {
  const { theme } = useTheme();
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const [media, setMedia] = useState<ExerciseMedia | null>(null);
  const [state, setState] = useState<LoadState>('loading');
  const [frame, setFrame] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [lang, setLang] = useState<DemoLang>(readStoredLang);
  const [steps, setSteps] = useState<string[]>([]);
  const [shownLang, setShownLang] = useState<DemoLang>('es');

  useEffect(() => {
    if (!exerciseName) return;

    const previouslyFocused =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const bodyAlreadyLocked = document.body.classList.contains('exercise-demo-scroll-lock');
    document.body.classList.add('exercise-demo-scroll-lock');
    closeButtonRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = Array.from(
        dialogRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR) ?? []
      );
      if (focusable.length === 0) {
        event.preventDefault();
        dialogRef.current?.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (!bodyAlreadyLocked) document.body.classList.remove('exercise-demo-scroll-lock');
      if (previouslyFocused?.isConnected) previouslyFocused.focus();
    };
  }, [exerciseName, onClose]);

  // Fetch media whenever the requested exercise changes.
  useEffect(() => {
    if (!exerciseName) return;
    let cancelled = false;
    setState('loading');
    setMedia(null);
    setSteps([]); // clear previous exercise's instructions so they never flash under a new title
    setFrame(0);
    setPlaying(true);
    getExerciseMedia(exerciseName)
      .then((result) => {
        if (cancelled) return;
        if (result && result.images.length > 0) {
          setMedia(result);
          setState('ready');
        } else {
          setState('notfound');
        }
      })
      .catch(() => {
        if (!cancelled) setState('error');
      });
    return () => {
      cancelled = true;
    };
  }, [exerciseName]);

  // Persist language choice and resolve the instructions in that language.
  useEffect(() => {
    try {
      localStorage.setItem(LANG_KEY, lang);
    } catch {
      /* ignore */
    }
    if (state !== 'ready' || !media) return;
    let cancelled = false;
    getInstructions(media, lang).then((res) => {
      if (cancelled) return;
      setSteps(res.steps);
      setShownLang(res.lang);
    });
    return () => {
      cancelled = true;
    };
  }, [lang, media, state]);

  // Alternate frames to fake a GIF (start ↔ end position).
  useEffect(() => {
    if (state !== 'ready' || !playing || !media || media.images.length < 2) return;
    const interval = setInterval(() => {
      setFrame((f) => (f + 1) % media.images.length);
    }, 1100);
    return () => clearInterval(interval);
  }, [state, playing, media]);

  if (!exerciseName) return null;

  return (
    <div className='fixed inset-0 z-[70] flex flex-col items-center justify-end'>
      <button
        type='button'
        tabIndex={-1}
        aria-label='Cerrar demo de ejercicio'
        className='absolute inset-0 bg-black/50'
        onClick={onClose}
      />
      <div
        ref={dialogRef}
        role='dialog'
        aria-modal='true'
        aria-labelledby={titleId}
        tabIndex={-1}
        className={`relative w-full max-w-lg max-h-[88vh] flex flex-col rounded-t-2xl ${theme.card} ${theme.text} border-t border-x ${theme.border} shadow-2xl`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag handle */}
        <div className='flex justify-center pt-3 pb-1'>
          <div className={`w-10 h-1 rounded-full ${theme.border} bg-current opacity-30`} />
        </div>

        {/* Header */}
        <div className={`flex items-center justify-between px-4 pb-3 border-b ${theme.border}`}>
          <h2 id={titleId} className='text-base font-semibold truncate pr-2'>
            {exerciseName}
          </h2>
          <div className='flex items-center gap-2 flex-shrink-0'>
            {/* ES / EN toggle */}
            <div className={`flex rounded-lg overflow-hidden border ${theme.border} p-0.5`}>
              {(['es', 'en'] as const).map((l) => (
                <button
                  key={l}
                  type='button'
                  onClick={() => setLang(l)}
                  className={`px-2.5 py-1 text-xs font-semibold rounded-md uppercase touch-manipulation transition-colors ${
                    lang === l ? 'bg-blue-600 text-white' : theme.textSecondary
                  }`}
                  aria-pressed={lang === l}
                >
                  {l}
                </button>
              ))}
            </div>
            <button
              ref={closeButtonRef}
              type='button'
              onClick={onClose}
              className={`p-1.5 rounded-lg touch-manipulation ${theme.textSecondary} ${theme.hover}`}
              aria-label='Cerrar'
            >
              <X className='w-5 h-5' />
            </button>
          </div>
        </div>

        <div className='flex-1 overflow-y-auto px-4 pt-4 pb-[calc(env(safe-area-inset-bottom)+1rem)] space-y-4'>
          {/* Media area — sizes itself to the image so there are no letterbox bands */}
          <div
            className={`relative w-full rounded-2xl overflow-hidden border ${theme.border}`}
          >
            {state === 'loading' && (
              <div className='aspect-square flex items-center justify-center animate-pulse text-sm text-gray-400'>
                Cargando demo…
              </div>
            )}

            {(state === 'notfound' || state === 'error') && (
              <div className='aspect-square flex flex-col items-center justify-center gap-2 text-gray-400 px-6 text-center'>
                <ImageOff className='w-10 h-10' />
                <p className='text-sm'>
                  {state === 'error'
                    ? 'No se pudo cargar la demo. Revisá tu conexión.'
                    : 'No hay demo para este ejercicio. Probá con el video de YouTube.'}
                </p>
              </div>
            )}

            {state === 'ready' && media && (
              <>
                {media.images.map((src, i) => (
                  <img
                    key={src}
                    src={src}
                    alt={`${media.name} posición ${i + 1}`}
                    loading='lazy'
                    className={`${
                      i === 0 ? 'block w-full h-auto' : 'absolute inset-0 w-full h-full object-cover'
                    } transition-opacity duration-500`}
                    style={{ opacity: i === frame ? 1 : 0 }}
                  />
                ))}
                {media.images.length > 1 && (
                  <button
                    type='button'
                    onClick={() => setPlaying((p) => !p)}
                    className='absolute bottom-2 right-2 w-9 h-9 rounded-full bg-black/60 text-white flex items-center justify-center backdrop-blur-sm touch-manipulation'
                    aria-label={playing ? 'Pausar' : 'Reproducir'}
                  >
                    {playing ? <Pause className='w-4 h-4' /> : <Play className='w-4 h-4 ml-0.5' />}
                  </button>
                )}
              </>
            )}
          </div>

          {/* Instructions */}
          {state === 'ready' && media && steps.length > 0 && (
            <div className='space-y-2'>
              <div className='flex items-center justify-between'>
                <h3 className={`text-xs font-semibold uppercase tracking-wide ${theme.textSecondary}`}>
                  {shownLang === 'es' ? 'Cómo hacerlo' : 'How to'}
                </h3>
                {lang === 'es' && shownLang === 'en' && (
                  <span className='text-[10px] text-amber-500'>traducción no disponible</span>
                )}
              </div>
              <ol className='space-y-2'>
                {steps.map((step, i) => (
                  <li key={i} className='flex gap-2.5 text-sm leading-snug'>
                    <span className='flex-shrink-0 w-5 h-5 rounded-full bg-blue-600/15 text-blue-500 text-[11px] font-bold flex items-center justify-center mt-0.5'>
                      {i + 1}
                    </span>
                    <span>{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* YouTube fallback / extra */}
          <a
            href={youtubeSearchUrl(exerciseName)}
            target='_blank'
            rel='noopener noreferrer'
            className='w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-red-600 text-white font-semibold text-sm hover:bg-red-700 active:bg-red-800 touch-manipulation'
          >
            <Youtube className='w-4 h-4' />
            Ver video en YouTube
          </a>
        </div>
      </div>
    </div>
  );
}
